import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { crm, type ProjectRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ProjectBoard } from '@/components/ProjectBoard';
import { ProjectDialog } from '@/components/ProjectDialog';
import { ProjectStatusPill } from '@/components/ProjectStatusPill';
import { formatEuro } from '@/lib/utils';

interface Props {
  customerId: string;
  organizationId: string;
}

type Ansicht = 'liste' | 'board';

/**
 * Projekte des Kunden — als Kartenliste oder als Board.
 *
 * Lädt eigenständig — anders als die übrigen Reiter, deren Daten aus der
 * Firmenakte kommen, denn `crm.projects.list` hängt am Kunden, nicht an der
 * Firma. Das Board fragt immer mit `includeClosed: true`: die beiden
 * Endzustände sind dort eigene Spalten, sonst blieben sie leer — der
 * „Abgeschlossene zeigen“-Schalter gilt deshalb nur für die Liste.
 */
export function ProjectsTab({ customerId, organizationId }: Props) {
  const navigate = useNavigate();
  const [ansicht, setAnsicht] = useState<Ansicht>('liste');
  const [projekte, setProjekte] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [anlegen, setAnlegen] = useState(false);

  const backTo = `/firmen/${organizationId}`;

  const load = useCallback(async () => {
    setLoading(true);
    const daten = await crm.projects.list({
      customerId,
      includeClosed: ansicht === 'board' ? true : includeClosed,
    });
    setProjekte(daten);
    setLoading(false);
  }, [customerId, includeClosed, ansicht]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {ansicht === 'liste' ? (
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
            />
            Abgeschlossene zeigen
          </label>
        ) : (
          <span />
        )}

        <div className="flex items-center gap-2">
          <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
            <button
              type="button"
              onClick={() => setAnsicht('liste')}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                ansicht === 'liste' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              Liste
            </button>
            <button
              type="button"
              onClick={() => setAnsicht('board')}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                ansicht === 'board' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              Board
            </button>
          </div>
          <Button size="sm" onClick={() => setAnlegen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Neues Projekt
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Lade …</p>
      ) : projekte.length === 0 ? (
        <Card className="space-y-1 p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Noch kein Projekt. „Neues Projekt“ legt das erste an.
          </p>
        </Card>
      ) : ansicht === 'board' ? (
        <ProjectBoard projects={projekte} backTo={backTo} onMoved={() => void load()} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {projekte.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer p-4 hover:bg-accent/40"
              onClick={() => navigate(`/projekte/${p.id}`, { state: { from: backTo } })}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <h3 className="truncate font-medium">{p.title}</h3>
                  {p.summary && (
                    <p className="truncate text-sm text-muted-foreground">{p.summary}</p>
                  )}
                </div>
                <ProjectStatusPill status={p.status} />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {p.dueDate && (
                  <span>Fällig {new Date(p.dueDate).toLocaleDateString('de-DE')}</span>
                )}
                {p.totalTasks > 0 && (
                  <span>
                    {p.totalTasks - p.openTasks}/{p.totalTasks} Aufgaben
                  </span>
                )}
                {p.orderCount > 0 && (
                  <span>
                    {p.orderCount} Aufträge · {formatEuro(p.orderValueCents)}
                  </span>
                )}
                {p.fileCount > 0 && <span>{p.fileCount} Dateien</span>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {anlegen && (
        <ProjectDialog
          customerId={customerId}
          onClose={() => setAnlegen(false)}
          onSaved={() => {
            setAnlegen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}
